import React, { useState, useEffect } from 'react'
import { Modal, Button, Input, DatePicker, Select, Upload, message, InputNumber } from 'antd'
import { UploadOutlined, EyeOutlined } from '@ant-design/icons'
import moment from 'moment'
import { ApiRequest } from '../../../../services/api/api'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import { getCookie } from 'react-use-cookie'
import type { UploadFile, RcFile } from 'antd/es/upload/interface'
import { baseUrl } from '../../../../services/api/base-url'
import PdfStickService from '../../../../services/others/pdf-sticker-services'
import { modalStyles, titleStyle, indicatorStyle, gradientHeaderStyle, headerItemStyle, headerLabelStyle, headerValueStyle, modalContainerStyle } from './modal-styles'
import InspectionLogModal from './inspection-log-modal'
import { getAuthHeaders } from '../../../../services/lib/utils/auth.utils'

const { TextArea } = Input

interface InspectionItemDetail {
  rawMaterialId: number
  itemCode: string
  description: string
  revAreaQty: number  // 接收区域数量
  putAwayQty: number  // 上架数量
}

interface PurchaseOrderReceivingModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  onSuccess: () => void
}

interface Employee {
  employeeId: number
  firstName: string
  lastName: string
}

const PurchaseOrderReceivingModal: React.FC<PurchaseOrderReceivingModalProps> = ({
  visible,
  onClose,
  poData,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [packingSlipNo, setPackingSlipNo] = useState('')
  const [arrivalTime, setArrivalTime] = useState<moment.Moment>(moment())
  const [receiver, setReceiver] = useState<number>(1) // 始终使用数字类型
  const [note, setNote] = useState('')
  const [fileList, setFileList] = useState<any[]>([])

  // 物料收货数量状态
  const [itemsReceiving, setItemsReceiving] = useState<any[]>([])

  // 员工列表状态
  const [employeeList, setEmployeeList] = useState<Employee[]>([])

  // 现有 inspection 数据状态（用于编辑模式）
  const [existingInspectionId, setExistingInspectionId] = useState<string | null>(null) // 改为字符串类型 (GUID)

  // 图片预览状态
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  // 包装信息映射
  const [packagingInfoMap, setPackagingInfoMap] = useState<Map<number, number>>(new Map())

  // Inspection Item 详情映射（用于显示 Putaway Qty 和 RevAreaQty）
  const [itemDetailsMap, setItemDetailsMap] = useState<Map<number, InspectionItemDetail>>(new Map())

  // Inspection Log Modal 状态
  const [inspectionLogVisible, setInspectionLogVisible] = useState(false)

  // 原材料 productId 信息映射（rawMaterialId -> productId）
  const [rawMaterialProductMap, setRawMaterialProductMap] = useState<Map<number, number | null>>(new Map())

  // 获取单个原材料的 productId
  const fetchRawMaterialProductId = async (rawMaterialId: number): Promise<number | null> => {
    try {
      const response = await ApiRequest({
        url: `RawMaterial/GetRawMaterialById?id=${rawMaterialId}`,
        method: 'get',
        isShowSpinner: false
      })

      const rawMaterialData = response?.data?.data
      return rawMaterialData?.productId ?? null
    } catch (error) {
      console.error(`Failed to fetch productId for raw material ${rawMaterialId}:`, error)
      return null
    }
  }

  // 预加载所有原材料的 productId 信息
  const preloadRawMaterialInfo = async () => {
    if (!poData?.poDetail) return

    const productMap = new Map<number, number | null>()

    // 收集所有唯一的 rawMaterialId
    const uniqueRawMaterialIds = new Set<number>()
    poData.poDetail.forEach((detail: any) => {
      if (detail.rawMaterial?.rawMaterialId) {
        uniqueRawMaterialIds.add(detail.rawMaterial.rawMaterialId)
      }
    })

    // 并行获取所有原材料的 productId
    const rawMaterialIdArray = Array.from(uniqueRawMaterialIds)
    const promises = rawMaterialIdArray.map(async (rawMaterialId) => {
      return await fetchRawMaterialProductId(rawMaterialId)
    })

    const results = await Promise.all(promises)

    rawMaterialIdArray.forEach((rawMaterialId, index) => {
      productMap.set(rawMaterialId, results[index])
    })

    setRawMaterialProductMap(productMap)
  }

  // 判断原材料是否是产品
  const isRawMaterialAProduct = (rawMaterialId: number): boolean => {
    const productId = rawMaterialProductMap.get(rawMaterialId);
    return productId !== null && productId !== undefined;
  };

  // 打开 Inspection Log Modal
  const handleOpenInspectionLog = () => {
    if (!existingInspectionId) {
      message.warning('Please save receiving data before viewing inspection log')
      return
    }
    setInspectionLogVisible(true)
  }

  // 获取员工列表
  useEffect(() => {
    const fetchEmployeeList = async () => {
      try {
        const response = await ApiRequest({
          url: 'Employee/GetAllEmployee',
          method: 'get',
          isShowSpinner: false
        })
        const employees = response?.data?.data || []
        setEmployeeList(employees)
      } catch (error) {
        setEmployeeList([])
      }
    }

    fetchEmployeeList()
  }, [])

  useEffect(() => {
    if (visible && poData) {
      // 重置状态
      setExistingInspectionId(null)

      // 设置当前用户为默认收货人
      const currentUserId = parseInt(getCookie('id') || '1')
      setReceiver(currentUserId)

      // 重置表单字段
      setPackingSlipNo('')          // Packing Slip No. 为空
      setArrivalTime(moment())      // Arrival Time 为当前时间
      setNote('')                   // Note 为空
      setFileList([])               // 图片列表为空

      // 初始化物料收货数量
      const initialItems = poData.poDetail?.map((item: any) => {
        const totalRevQty = item.totalRevQty || 0
        const putawayQty = item.totalHandledQty || 0
        const revAreaQty = Math.max(0, totalRevQty - putawayQty) // 接收区域的数量
        return {
          poDetailId: item.poDetailId,
          currentRevQty: revAreaQty,   // 当前接收区域的数量
          totalRevQty: totalRevQty,     // 总收货数量
          putawayQty: putawayQty,       // 已上架数量
          receivingAdd: revAreaQty      // 本次新收货数量，默认等于当前接收区域的数量
        }
      }) || []
      setItemsReceiving(initialItems)

      // 异步初始化数据
      const initializeData = async () => {
        // 预加载所有原材料的 productId 和 unit 信息
        await preloadRawMaterialInfo()

        // 获取已保存的 UnloadingInspection 数据（包含 RevAreaQty 和 PutAwayQty）
        await fetchExistingUnloadingInspection()

        // 获取所有物料的包装信息
        fetchAllPackagingInfo()
      }

      initializeData()
    }
  }, [visible, poData])

  // 获取所有物料的包装信息
  const fetchAllPackagingInfo = async () => {
    if (!poData?.poDetail) return

    const newPackagingMap = new Map<number, number>()

    // 并行获取所有物料的包装信息（两步流程）
    const promises = poData.poDetail.map(async (item: any) => {
      if (!item.rawMaterial?.rawMaterialId) return

      try {
        // 第一步：通过 rawMaterialId 获取 baseProductId
        const rawMaterialResponse = await ApiRequest({
          url: `RawMaterial/GetRawMaterialById?id=${item.rawMaterial.rawMaterialId}`,
          method: 'get',
          isShowSpinner: false
        })

        if (rawMaterialResponse?.data?.data) {
          const rawMaterialData = rawMaterialResponse.data.data

          // 从 baseProductRawMaterial 数组中获取第一个 baseProductId
          const baseProductRawMaterial = rawMaterialData.baseProductRawMaterial?.[0]
          const baseProductId = baseProductRawMaterial?.baseProductId

          if (baseProductId) {
            // 第二步：通过 baseProductId 获取基本产品的包装信息
            const baseProductResponse = await ApiRequest({
              url: `BaseProduct/GetBaseProductById?id=${baseProductId}`,
              method: 'get',
              isShowSpinner: false
            })

            if (baseProductResponse?.data?.data) {
              const baseProduct = baseProductResponse.data.data
              // 优先级：packagingType.quantity > productPrice[0].quantiy > quantity
              const packagingQty = baseProduct.packagingType?.quantity ||
                                 baseProduct.productPrice?.[0]?.quantiy ||
                                 baseProduct.quantity || 0
              newPackagingMap.set(item.poDetailId, packagingQty)
            }
          } else {
            // 回退：使用 rawMaterial 的包装信息
            const packagingQty = rawMaterialData.quantity || rawMaterialData.packagingQty || 0
            newPackagingMap.set(item.poDetailId, packagingQty)
          }
        }
      } catch (error) {
        // Silently handle fetch errors
      }
    })

    await Promise.all(promises)
    setPackagingInfoMap(newPackagingMap)
  }

  // 获取物料的 Putaway Qty
  const getPutawayQty = (item: any) => {
    const itemDetail = itemDetailsMap.get(item.poDetailId);
    return itemDetail?.putAwayQty || 0;
  };

  // 获取物料的 Qty/unit（根据是否是产品使用不同逻辑）
  const getQtyPerUnit = (item: any): string | number => {
    const rawMaterialId = item.rawMaterial?.rawMaterialId;
    const isProduct = rawMaterialId ? isRawMaterialAProduct(rawMaterialId) : false;

    if (isProduct) {
      // 产品：使用原来的逻辑（从 baseProduct 获取 packagingQty）
      return packagingInfoMap.get(item.poDetailId) ||
             item.rawMaterial?.baseProductRawMaterial?.[0]?.packagingQty ||
             item.rawMaterial?.baseProductRawMaterial?.[0]?.quantity ||
             item.rawMaterial?.packagingQty ||
             item.rawMaterial?.quantity || 0;
    } else {
      // 原材料：取 po_detail 的 unit
      return item.unit || item.rawMaterial?.unit || '';
    }
  };



  // 获取已保存的 UnloadingInspection 数据
  const fetchExistingUnloadingInspection = async () => {
    try {
      const response = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (response?.data?.data && response.data.data.length > 0) {
        const inspectionData = response.data.data[0]

        // 保存 inspectionId
        if (inspectionData.inspectionId) {
          setExistingInspectionId(inspectionData.inspectionId)
        }

        // 提取 unloadingDetail 中的 revAreaQty 和 putAwayQty，并填充物料收货数量
        if (inspectionData.unloadingDetail && Array.isArray(inspectionData.unloadingDetail)) {
          const newItemDetailsMap = new Map<number, InspectionItemDetail>()

          inspectionData.unloadingDetail.forEach((detail: any) => {
            // 通过 rawMaterialId 找到对应的 poDetail
            const poDetailItem = poData.poDetail?.find((poDetail: any) =>
              poDetail.rawMaterial?.rawMaterialId === detail.rawMaterialId
            )

            if (poDetailItem) {
              newItemDetailsMap.set(poDetailItem.poDetailId, {
                rawMaterialId: detail.rawMaterialId,
                itemCode: poDetailItem.rawMaterial?.rawMaterialCode || '',
                description: poDetailItem.rawMaterial?.rawMaterialName || '',
                revAreaQty: detail.revAreaQty || 0,
                putAwayQty: detail.putAwayQty || 0
              })
            }
          })

          setItemDetailsMap(newItemDetailsMap)
// debugger
          // 填充物料收货数量 - 直接使用 newItemDetailsMap（避免异步状态更新问题）
          const updatedItems = poData.poDetail?.map((item: any) => {
            // 直接使用 newItemDetailsMap 而不是 itemDetailsMap 状态（避免异步状态更新问题）
            const itemDetail = newItemDetailsMap.get(item.poDetailId)
            const revAreaQty = itemDetail?.revAreaQty || 0
            const putAwayQty = itemDetail?.putAwayQty || 0

            return {
              poDetailId: item.poDetailId,
              currentRevQty: revAreaQty,  // 当前接收区域的数量
              putawayQty: putAwayQty,     // 已上架数量
              totalRevQty: item.totalRevQty || 0,
              receivingAdd: revAreaQty     // 本次新收货数量，默认等于当前接收区域的数量
            }
          }) || []
          setItemsReceiving(updatedItems)
        }

        // 不再加载已保存的表单数据，保持为空或默认值
        // Packing Slip No. 和 Note 保持为空
        // Arrival Time 保持为当前时间（已在 useEffect 中设置）
        // Receiver 保持为登录的操作人员（已在 useEffect 中设置）

        // 注释掉原来的表单数据加载逻辑
        // if (inspectionData.containerNo) {
        //   setPackingSlipNo(inspectionData.containerNo)
        // }
        // if (inspectionData.arrivalTime) {
        //   setArrivalTime(moment(inspectionData.arrivalTime))
        // }
        // // 设置收货人 - 确保使用数字类型的 receiverId
        // if (inspectionData.receiverId) {
        //   const receiverId = Number(inspectionData.receiverId)
        //   setReceiver(receiverId)
        // } else if (inspectionData.receiver?.employeeId) {
        //   // 如果有 receiver 对象，使用其中的 employeeId
        //   setReceiver(inspectionData.receiver.employeeId)
        // } else if (inspectionData.unloadingPeople) {
        //   // 尝试将 unloadingPeople 转换为数字
        //   const parsedId = parseInt(inspectionData.unloadingPeople)
        //   if (!isNaN(parsedId)) {
        //     setReceiver(parsedId)
        //   }
        // }

        // 注释掉 notes 和图片的加载逻辑，保持为空
        // if (inspectionData.conditionOfProduct) {
        //   setNote(inspectionData.conditionOfProduct)
        // } else if (inspectionData.notes) {
        //   setNote(inspectionData.notes)
        // }

        // 处理图片 - 也注释掉，保持为空
        // if (inspectionData.images && inspectionData.images !== '[]') {
        //   try {
        //     const imagesData = JSON.parse(inspectionData.images)
        //     if (Array.isArray(imagesData) && imagesData.length > 0) {
        //       const fileList = imagesData.map((img: any, index: number) => ({
        //         uid: index.toString(),
        //         name: 'image.png',
        //         status: 'done',
        //         url: img.url
        //       }))
        //       setFileList(fileList)
        //     }
        //   } catch (e) {
        //     // Ignore JSON parse errors
        //   }
        // }
      } else {
        // 没有现有数据，重置为新建模式
        setExistingInspectionId(null)
      }
    } catch (error) {
      setExistingInspectionId(null)
    }
  }

  // 获取某个物料的当前收货数量（始终显示 API 的值，不随用户操作变化）
  const getDisplayReceivingQty = (itemIndex: number) => {
    const item = poData?.poDetail?.[itemIndex]
    const itemDetail = itemDetailsMap.get(item?.poDetailId)
    const revAreaQty = itemDetail?.revAreaQty || 0
    return revAreaQty
  }

  // 更新正数输入框 - 输入框显示的是最终的收货数量
  const handleReceivingAddChange = (index: number, value: number) => {
    const newItems = [...itemsReceiving]
    if (!newItems[index]) return

    const poDetailItem = poData?.poDetail?.[index]
    const currentRevQty = newItems[index].currentRevQty


    // 设置本次新收货数量
    newItems[index].receivingAdd = value

    // 移除验证：允许本次新收货数量为0
 
    if (value <0) {
      message.error(`New receiving quantity must be greater than 0 (Current: ${currentRevQty}, Input: ${value})`)
      return
    }

    // 验证：最终收货数量 (PutawayQty + Latest Receiving Qty) 不能超过 PO Qty
    const putawayQty = newItems[index].putawayQty || 0
    const finalQty = putawayQty + value
    setItemsReceiving(newItems)
  }


  // 打印产品 sticker
  const handlePrintLabel = async (poDetailId: number) => {
    try {
      // 获取 inspectionId
      let inspectionId = poData?.inspectionId
      if (!inspectionId) {
        const inspectionResponse = await ApiRequest({
          url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
          method: 'get',
          isShowSpinner: false
        })
        if (inspectionResponse?.data?.data && inspectionResponse.data.data.length > 0) {
          inspectionId = inspectionResponse.data.data[0]?.inspectionId
        }
      }

      if (!inspectionId) {
        message.warning('Cannot get Inspection ID, please complete receiving process first')
        return
      }

      // 获取 box 详情
      const boxResponse = await ApiRequest({
        url: `UnloadingInspection/GetBoxDetailById?id=${inspectionId}`,
        method: 'get',
        isShowSpinner: true
      })

      if (boxResponse?.data?.data) {
        const data = boxResponse.data.data
        const poDetailItem = poData?.poDetail?.find((detail: any) => detail.poDetailId === poDetailId)

        if (poDetailItem) {
          const itemDetail = (data.items || []).find((item: any) =>
            item.rawMaterialId === poDetailItem.rawMaterial?.rawMaterialId ||
            item.itemCode === poDetailItem.rawMaterial?.rawMaterialCode
          )

          if (itemDetail && itemDetail.boxes && itemDetail.boxes.length > 0) {
            // 转换 boxes 数据为 PdfStickService 期望的格式
            const boxesForPdf = await convertBoxesToPdfFormat(itemDetail.boxes, poDetailItem)

            // 获取 logo base64
            const logoBase64 = await fetchLogoBase64()

            // 调用打印服务
            PdfStickService(boxesForPdf, logoBase64)
            message.success(`Successfully printed ${boxesForPdf.length} box stickers`)
          } else {
            message.warning('This item has no box information, cannot print box stickers')
          }
        }
      }
    } catch (error) {
      console.error('Print failed:', error)
      message.error('Box sticker print failed')
    }
  }

  // 将 boxes 数据转换为 PdfStickService 期望的格式
  const convertBoxesToPdfFormat = async (boxes: any[], poDetailItem: any): Promise<any[]> => {
    // 获取产品信息
    const rawMaterialId = poDetailItem.rawMaterial?.rawMaterialId

    let productInfo: any = null
    try {
      const rawMaterialResponse = await ApiRequest({
        url: `RawMaterial/GetRawMaterialById?id=${rawMaterialId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (rawMaterialResponse?.data?.data) {
        const rawMaterialData = rawMaterialResponse.data.data
        const productId = rawMaterialData.productId

        if (productId) {
          const productResponse = await ApiRequest({
            url: `Product/GetProductById?id=${productId}`,
            method: 'get',
            isShowSpinner: false
          })

          if (productResponse?.data?.data) {
            productInfo = productResponse.data.data
          }
        }
      }
    } catch (error) {
      // Handle fetch errors silently
    }

    // 转换每个 box
    return boxes.map(box => ({
      barCode: box.barCode,
      product: productInfo || {
        productName: poDetailItem.rawMaterial?.rawMaterialName || 'N/A',
        productCode: poDetailItem.rawMaterial?.rawMaterialCode || 'N/A',
        custProductCode: poDetailItem.rawMaterial?.rawMaterialCode || 'N/A',
        logoType: 1,
        logoUrl: '',
        baseProduct: {
          packagingType: {
            sleevePkt: 0,
            sleeveQty: 0
          },
          grossWeigh: 0,
          cartonLength: 0,
          cartonWidth: 0,
          cartonHeight: 0
        }
      },
      custProductCode: poDetailItem.rawMaterial?.rawMaterialCode || '',
      quantity: 1, // 每个 box 代表 1 个单位
      workOrderNo: poData?.poNumber || '',
      createdAt: new Date().toISOString()
    }))
  }

  // 获取 logo base64
  const fetchLogoBase64 = async (): Promise<string> => {
    try {
      const response = await fetch('/logo.png')
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      return ''
    }
  }

  // 上传配置（参考 UnloadingInspectionAddDialog）
  const onChange = (newFileList: any) => {
    setFileList(newFileList.fileList)
  }

  const handleCancelPreview = () => setPreviewVisible(false)

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => reject(error)
    })

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile)
    }
    setPreviewImage(file.url || (file.preview as string))
    setPreviewVisible(true)
  }

  // 取消操作
  const handleCancel = () => {
    // 重置表单
    setPackingSlipNo('')
    setArrivalTime(moment())
    const currentUserId = parseInt(getCookie('id') || '1')
    setReceiver(currentUserId)
    setNote('')
    setFileList([])
    setItemsReceiving([])
    setExistingInspectionId(null)
    onClose()
  }

  // 更新收货
  const handleUpdate = async () => {
    // 验证必填项
    if (!packingSlipNo.trim()) {
      message.error('Please enter Packing Slip No.')
      return
    }

    // 检查是否有数据变化（收货数量或其他信息）
    const hasReceivingQty = itemsReceiving.some(item => {
      return item.receivingAdd > 0
    })

    // 如果没有收货数量变化，允许保存其他信息（Note、PackingSlipNo等）
    // 只要有 PackingSlipNo（必填项）就可以保存

    setLoading(true)

    try {
      // 准备提交数据 - 按照 UnloadingInspection 的格式
      const receivingData = {
        poId: poData.poId,
        containerNo: packingSlipNo.trim(),
        arrivalTime: arrivalTime.toISOString(),
        receiverId: receiver, // 使用 receiverId (数字)
        unloadingPeople: receiver.toString(), // 同时保留 unloadingPeople (字符串)
        conditionOfProduct: note.trim(),
        notes: note.trim(), // 同时保留 notes 字段
        autoGenerateBox: true, // 自动生成箱子
        // 文件上传处理
        images: fileList.length > 0 ? JSON.stringify(fileList.map((file: any) => ({
          uid: file.uid,
          url: file.url || file.response // 使用已上传的 URL 或服务器响应的 URL
        }))) : null,
        // 物料收货明细 - 使用 unloadingDetail 格式（发送所有 Item，不管有没有修改）
        unloadingDetail: itemsReceiving.map((item: any, index: number) => {
            const poDetailItem = poData.poDetail?.find((detail: any) => detail.poDetailId === item.poDetailId)
            const latestReceivingQty = item.receivingAdd // 本次新收货数量
            const putawayQty = item.putawayQty || 0 // 已上架数量
            const finalQty = putawayQty + latestReceivingQty // 最终总收货数量 = PutawayQty + Latest Receiving Qty

            return {
              rawMaterialId: poDetailItem?.rawMaterial?.rawMaterialId,
              revQuantity: finalQty, // 最终的总收货数量 = PutawayQty + Latest Receiving Qty
              quantity: finalQty // PutawayQty + Latest Receiving Qty
            }
          })
      }

      // 根据是否有现有数据决定是创建还是更新
      if (existingInspectionId) {
        // 更新模式 - 将 inspectionId 添加到数据中
        await ApiRequest({
          url: 'UnloadingInspection/UpdateUnloadingInspection',
          method: 'put',
          data: {
            ...receivingData,
            inspectionId: existingInspectionId
          },
          isShowSpinner: true
        })
      } else {
        // 创建模式 - 获取返回的 inspectionId
        const response = await ApiRequest({
          url: 'UnloadingInspection/addUnloadingInspection',
          method: 'post',
          data: receivingData,
          isShowSpinner: true
        })
        // 保存新创建的 inspectionId 以供后续使用
        if (response?.data?.data?.inspectionId) {
          setExistingInspectionId(response.data.data.inspectionId)
        }
      }

      await SweetAlertService.successMessage('Receiving update successful')

      // 重新获取 Inspection 数据以更新 RevAreaQty 和 PutAwayQty
      await fetchExistingUnloadingInspection()

      // 重置表单并关闭
      handleCancel()
      onSuccess() // 刷新列表

    } catch (error) {
      message.error('Receiving update failed')
    } finally {
      setLoading(false)
    }
  }

  // Upload 组件配置
  const uploadProps = {
    name: 'imageFile',
    listType: 'picture-card' as const,
    action: baseUrl + 'Common/UploadImage',
    headers: getAuthHeaders(),
    fileList: fileList,
    onPreview: handlePreview,
    onChange: onChange,
    maxCount: 5
  }

  return (
    <>
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      width={1600}
      footer={null}
      closable={true}
      style={{ top: 20 }}
      bodyStyle={{ padding: '20px', height: 'calc(100vh - 140px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          PO Receiving Board
        </div>

        {/* 基础信息区 */}
        <div style={gradientHeaderStyle}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>PO Number:</span>
              <span style={headerValueStyle}>
                {poData?.poNo || 'N/A'}
              </span>
            </div>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>Supplier:</span>
              <span style={headerValueStyle}>
                {poData?.suplier?.suplierName || 'N/A'}
              </span>
            </div>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>Creator:</span>
              <span style={headerValueStyle}>
                {poData?.createdEmployee?.firstName} {poData?.createdEmployee?.lastName || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* 物料明细录入列表 */}
        <div style={{ marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', backgroundImage: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)' }}>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Item Code</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Qty/unit</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>PO Qty</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Putaway Qty</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>QTY in Inspection</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>New Inspection Qty</th>
              </tr>
            </thead>
            <tbody>
              {poData?.poDetail?.map((item: any, index: number) => {
                const rawMaterialId = item.rawMaterial?.rawMaterialId
                const isProduct = rawMaterialId ? isRawMaterialAProduct(rawMaterialId) : false

                return (
                  <tr key={item.poDetailId} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', transition: 'background-color 0.2s' }}>
                    <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', color: '#334155' }}>
                      {item.rawMaterial?.rawMaterialCode || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', maxWidth: '300px', color: '#334155' }}>
                      <div style={{ wordBreak: 'break-word' }}>
                        {item.rawMaterial?.rawMaterialName || 'N/A'}
                      </div>
                    </td>
                    <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#334155', fontWeight: 500 }}>
                      {getQtyPerUnit(item)}
                    </td>
                    <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#334155', fontWeight: 500 }}>
                      {item.quantity || 0}
                    </td>
                    {/* 只有产品才显示 Putaway Qty */}
                    {isProduct ? (
                      <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#f59e0b' }}>{getPutawayQty(item)}</span>
                      </td>
                    ) : (
                      <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#9ca3af' }}>
                        <span style={{ fontSize: '14px', fontWeight: 400 }}>-</span>
                      </td>
                    )}
                    <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>{getDisplayReceivingQty(index)}</span>
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <InputNumber
                        size="small"
                        min={0}
                        max={poData?.poDetail?.[index]?.quantity || 0}
                        value={itemsReceiving[index]?.receivingAdd || 0}
                        onChange={(value) => handleReceivingAddChange(index, value || 0)}
                        style={{ width: '100px' }}
                        placeholder="Enter qty"
                      />
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* 收货日志/凭证录入区 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: '#ef4444', marginRight: '4px' }}>*</span>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Packing Slip No.</span>
                </div>
                <Input
                  placeholder="请输入装箱单号"
                  value={packingSlipNo}
                  onChange={(e) => setPackingSlipNo(e.target.value)}
                  maxLength={50}
                />
              </div>
              <div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Arrival Time</span>
                </div>
                <DatePicker
                  showTime
                  value={arrivalTime}
                  onChange={(date) => setArrivalTime(date || moment())}
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </div>
              <div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Receiver</span>
                </div>
                <Select
                  value={receiver}
                  onChange={(value) => {
                    setReceiver(value)
                  }}
                  style={{ width: '100%' }}
                  placeholder="Select receiver"
                  showSearch
                  optionFilterProp="children"
                >
                  {employeeList.map((employee) => (
                    <Select.Option key={employee.employeeId} value={employee.employeeId}>
                      {employee.firstName} {employee.lastName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Note (special Qty or condition info)</span>
                </div>
                <TextArea
                  rows={2}
                  placeholder="请输入备注信息"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                />
              </div>
              <div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Upload photo</span>
                </div>
                <Upload {...uploadProps}>
                  {fileList.length < 5 && '+ Upload'}
                </Upload>
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作按钮区 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
          <Button
            icon={<EyeOutlined />}
            onClick={handleOpenInspectionLog}
            style={{
              backgroundColor: '#fff',
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              minWidth: '140px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3e8ff';
              e.currentTarget.style.borderColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.borderColor = '#8b5cf6';
            }}
          >
            View Log
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              onClick={handleCancel}
              style={{
                backgroundColor: '#fff',
                borderColor: '#3b82f6',
                color: '#3b82f6',
                minWidth: '120px',
                lineHeight: '1.5',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUpdate}
              loading={loading}
              style={{
                backgroundColor: '#3b82f6',
                borderColor: '#3b82f6',
                minWidth: '120px',
                lineHeight: '1.5',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    </Modal>

    {/* 图片预览 Modal - 独立于主 Modal */}
    <Modal
      visible={previewVisible}
      title="Preview"
      footer={null}
      onCancel={handleCancelPreview}
      width={1200}
      style={{ top: 20 }}
    >
      <img
        alt="preview"
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '80vh',
          objectFit: 'contain',
          display: 'block'
        }}
        src={previewImage}
      />
    </Modal>

    {/* Inspection Log Modal */}
    <InspectionLogModal
      visible={inspectionLogVisible}
      onClose={() => setInspectionLogVisible(false)}
      inspectionId={existingInspectionId || ''}
    />
    </>
  )
}

export default PurchaseOrderReceivingModal
